import React, { useEffect, useMemo } from 'react';
import pluralize from 'pluralize';

import css from './InactiveEmailAlert.module.css';
import { Alert, AlertPropsType, Button } from '@mm/components';
import { BotBuilderRoutes } from '../../../routes/bot-builder-routes';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { activeChatbotIdSelector } from '@mm/state/selectors/viewSelectors';
import { selectCampaignById } from '@mm/state/selectors/campaignSelectors';
import { selectActiveEmailAccounts } from '@mm/state/selectors/emailAccountsSelectors';
import { AutomationState } from '../../features/beta/modules/Automations/AutomationEdit/use-automation';
import { EmailAccountKinds } from '@mm/state/interfaces/emailAccount';
import { isEqual } from 'lodash';

export const InactiveEmailAlert = (
  props: AlertPropsType & {
    state: AutomationState;
    onChange: (showAlert: boolean) => void;
  },
) => {
  const { state, onChange } = props;
  const history = useHistory();
  const accounts = useSelector(selectActiveEmailAccounts);
  const chatbotId = useSelector(activeChatbotIdSelector);
  const campaignId = state.source?.id;
  const storeCampaign = useSelector(state =>
    selectCampaignById(state, campaignId || -1),
  );
  const defaultEmailAccountsIds = storeCampaign?.emailAccountsIds || [];

  const showInactiveEmailAlert = useMemo(() => {
    if (!defaultEmailAccountsIds?.length && !accounts?.length) {
      return {
        show: true,
        plural: true,
      };
    }

    const usedAccount = defaultEmailAccountsIds
      .map(emailId => accounts.find(a => a.id === emailId))
      .filter(Boolean);

    const accountsWithErrors = usedAccount.filter(
      account =>
        account?.kind === 'gmail' &&
        account?.credential &&
        !account?.credential?.fullGmailScope,
    );

    const disconnectedAccounts = defaultEmailAccountsIds
      .map(emailId =>
        state.source?.emailAccounts?.find(
          a =>
            a.id === emailId &&
            a.kind === ('disconnected' as EmailAccountKinds),
        ),
      )
      .filter(Boolean);
    const sortedDisconnectedAccountsIds = disconnectedAccounts
      .map(a => a?.id)
      .sort();

    return {
      show:
        !!accountsWithErrors.length ||
        (!!disconnectedAccounts.length &&
          isEqual(
            sortedDisconnectedAccountsIds,
            defaultEmailAccountsIds.sort(),
          )),
      plural: !!accountsWithErrors.length,
    };
  }, [defaultEmailAccountsIds.join(''), accounts, state.source?.emailAccounts]);

  const inactiveEmail = showInactiveEmailAlert?.show;

  useEffect(() => {
    onChange(showInactiveEmailAlert.show);
  }, [showInactiveEmailAlert.show]);

  if (!inactiveEmail) return null;

  return (
    <Alert
      className={css.container}
      borderRadius={0}
      showCloseIcon={false}
      type="warning"
      showHeader
      {...props}
    >
      <div>
        <span className={css.title}>
          Action Required: Reconnect Your{' '}
          {pluralize('Email', showInactiveEmailAlert.plural ? 2 : 1)}
        </span>
        <span className={css.subtitle}>
          To continue sending emails, please reconnect your email in your
          account settings
        </span>

        <Button
          onClick={e => {
            e?.preventDefault();
            history.push(BotBuilderRoutes.integrations.getLink({ chatbotId }), {
              kind: 'email',
            });
          }}
        >
          Fix {pluralize('Email', showInactiveEmailAlert.plural ? 2 : 1)}
        </Button>
      </div>
    </Alert>
  );
};
